/**
 * NotificationEmpfaengerModal — Verwalter fuer Benachrichtigungs-Empfaenger (Feedbackschleifen Phase 2, #1553).
 *
 * Generische Empfaenger-Registry: pro Kategorie (aktuell nur 'Abwesenheits-Antrag') eine Empfaenger-Liste
 * mit zwei Kanal-Toggles (E-Mail / Telegram) + Person-hinzufuegen-Picker + Entfernen.
 * Telegram-Erreichbarkeit kommt aus BEIDEN Quellen (Legacy chat_id ODER verifizierter Bot-User) — Warn-Badge
 * wenn Telegram aktiviert aber Person nicht erreichbar. KEIN Versand (Phase 4), KEIN Abwesenheits-Hook (Phase 3).
 */
import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { X, Plus, Trash2, AlertTriangle, Mail, Send, Search } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

export default function NotificationEmpfaengerModal({ onClose, context }) {
  const { t } = useLanguage();
  // #1561: Scope = Abteilung (abteilungId number) ODER global (abteilungId null/undefined).
  const abteilungId = context?.abteilungId ?? null;
  const scopeLabel = context?.label || t('notifEmpf.scopeGlobalLabel', 'Global (alle Abteilungen)');
  const abtParam = abteilungId == null ? 'global' : String(abteilungId);
  const [kategorien, setKategorien] = useState([]);
  const [aktiveKat, setAktiveKat] = useState(null);
  const [empfaenger, setEmpfaenger] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [picker, setPicker] = useState([]);
  const [pickerSuche, setPickerSuche] = useState('');
  const [pickerSel, setPickerSel] = useState({});

  const loadEmpfaenger = useCallback((katId) => {
    fetch(`${API}/api/notification-kategorien/${katId}/empfaenger?abteilung_id=${abtParam}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setEmpfaenger(Array.isArray(d) ? d : []));
  }, [abtParam]);

  useEffect(() => {
    fetch(`${API}/api/notification-kategorien`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        const list = Array.isArray(d) ? d : [];
        setKategorien(list);
        if (list.length) {
          setAktiveKat(list[0].id);
          loadEmpfaenger(list[0].id);
        }
      })
      .finally(() => setLoading(false));
  }, [loadEmpfaenger]);

  const selectKat = (katId) => {
    setAktiveKat(katId);
    loadEmpfaenger(katId);
  };

  const openPicker = () => {
    setPickerSel({});
    setPickerSuche('');
    setPickerOpen(true);
    fetch(`${API}/api/notification-kategorien/mitarbeiter-picker`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setPicker(Array.isArray(d) ? d : []));
  };

  const addSelected = async () => {
    const ids = Object.keys(pickerSel).filter(id => pickerSel[id]).map(Number);
    for (const mid of ids) {
      await fetch(`${API}/api/notification-kategorien/${aktiveKat}/empfaenger`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mitarbeiter_id: mid, kanal_email: true, kanal_telegram: false, abteilung_id: abteilungId })
      });
    }
    setPickerOpen(false);
    loadEmpfaenger(aktiveKat);
  };

  const toggleKanal = async (e, feld) => {
    const neu = !e[feld];
    await fetch(`${API}/api/notification-empfaenger/${e.id}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ [feld]: neu })
    });
    setEmpfaenger(prev => prev.map(x => x.id === e.id ? { ...x, [feld]: neu } : x));
  };

  const removeEmpf = async (e) => {
    await fetch(`${API}/api/notification-empfaenger/${e.id}`, { method: 'DELETE', credentials: 'include' });
    setEmpfaenger(prev => prev.filter(x => x.id !== e.id));
  };

  const vorhandeneIds = new Set(empfaenger.map(e => e.mitarbeiter_id));
  const pickerGefiltert = picker.filter(p => {
    if (vorhandeneIds.has(p.id)) return false;
    if (!pickerSuche.trim()) return true;
    const q = pickerSuche.toLowerCase();
    return (p.mitarbeiter_name || '').toLowerCase().includes(q) || (p.email || '').toLowerCase().includes(q);
  });

  const Toggle = ({ on, onClick, label, icon }) => (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${
        on
          ? 'bg-[#338edc]/10 border-[#338edc] text-[#338edc]'
          : 'bg-[var(--bg-inset,#F8F9FC)] border-[var(--border-default,#E5E7EB)] text-[var(--text-tertiary)]'
      }`}
    >
      {icon}{label}
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="bg-[var(--bg-card,#fff)] rounded-2xl w-full max-w-2xl max-h-[85vh] flex flex-col shadow-xl"
        onClick={ev => ev.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-default,#E5E7EB)]">
          <div>
            <h2 className="text-lg font-bold text-[var(--text-primary)]">
              {t('notifEmpf.title', 'Benachrichtigungs-Empfänger verwalten')}
            </h2>
            <p className="text-xs text-[var(--text-secondary)] mt-0.5">
              {t('notifEmpf.subtitle', 'Wer wird pro Kategorie per E-Mail oder Telegram benachrichtigt')}
            </p>
            <span className="inline-flex items-center gap-1.5 mt-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-[#338edc]/10 text-[#338edc]">
              {abteilungId == null
                ? t('notifEmpf.scopeGlobalBadge', 'Global')
                : t('notifEmpf.scopeDeptBadge', 'Abteilung')}: {scopeLabel}
            </span>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[var(--bg-inset,#F8F9FC)]">
            <X size={18} className="text-[var(--text-tertiary)]" />
          </button>
        </div>

        {loading ? (
          <div className="p-8 text-center text-[var(--text-tertiary)]">{t('common.loading', 'Lädt…')}</div>
        ) : (
          <div className="flex flex-1 min-h-0">
            {/* Kategorien-Liste */}
            <div className="w-48 border-r border-[var(--border-default,#E5E7EB)] p-3 overflow-y-auto">
              <div className="text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] mb-2 px-1">
                {t('notifEmpf.kategorien', 'Kategorien')}
              </div>
              {kategorien.map(k => (
                <button
                  key={k.id}
                  onClick={() => selectKat(k.id)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm mb-1 transition-colors ${
                    aktiveKat === k.id
                      ? 'bg-[#338edc]/10 text-[#338edc] font-medium'
                      : 'text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)]'
                  }`}
                >
                  {k.label_de || k.schluessel}
                </button>
              ))}
            </div>

            {/* Empfaenger-Liste */}
            <div className="flex-1 p-4 overflow-y-auto min-w-0">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold text-[var(--text-primary)]">
                  {t('notifEmpf.empfaenger', 'Empfänger')} ({empfaenger.length})
                </span>
                <button
                  onClick={openPicker}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-[#338edc] text-white hover:bg-[#2b7bc4]"
                >
                  <Plus size={14} />{t('notifEmpf.personHinzufuegen', 'Person hinzufügen')}
                </button>
              </div>

              {empfaenger.length === 0 && (
                <div className="text-center text-xs text-[var(--text-tertiary)] py-8">
                  {t('notifEmpf.keineEmpfaenger', 'Noch keine Empfänger. Über „Person hinzufügen" festlegen.')}
                </div>
              )}

              <div className="space-y-2">
                {empfaenger.map(e => {
                  const telegramWarn = e.kanal_telegram && !e.telegram_erreichbar;
                  const emailWarn = e.kanal_email && !e.email;
                  return (
                    <div key={e.id} className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-[var(--text-primary)] truncate">
                            {e.mitarbeiter_name || `${e.vorname || ''} ${e.nachname || ''}`.trim()}
                          </div>
                          <div className="text-xs text-[var(--text-tertiary)] truncate">
                            {e.email || t('notifEmpf.keineEmail', 'keine E-Mail hinterlegt')}
                          </div>
                        </div>
                        <button
                          onClick={() => removeEmpf(e)}
                          className="p-1.5 rounded-lg hover:bg-red-50 text-[var(--text-tertiary)] hover:text-red-600 shrink-0"
                          title={t('notifEmpf.entfernen', 'Entfernen')}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 mt-2.5">
                        <Toggle
                          on={e.kanal_email}
                          onClick={() => toggleKanal(e, 'kanal_email')}
                          label={t('notifEmpf.email', 'E-Mail')}
                          icon={<Mail size={13} />}
                        />
                        <Toggle
                          on={e.kanal_telegram}
                          onClick={() => toggleKanal(e, 'kanal_telegram')}
                          label={t('notifEmpf.telegram', 'Telegram')}
                          icon={<Send size={13} />}
                        />
                      </div>
                      {telegramWarn && (
                        <div className="flex items-center gap-1.5 mt-2 text-xs text-amber-700 bg-amber-50 rounded-lg px-2.5 py-1.5">
                          <AlertTriangle size={13} className="shrink-0" />
                          {t('notifEmpf.telegramWarn', 'Telegram nicht verbunden – Person erhält keine Telegram-Nachricht')}
                        </div>
                      )}
                      {emailWarn && (
                        <div className="flex items-center gap-1.5 mt-2 text-xs text-amber-700 bg-amber-50 rounded-lg px-2.5 py-1.5">
                          <AlertTriangle size={13} className="shrink-0" />
                          {t('notifEmpf.emailWarn', 'Keine E-Mail hinterlegt – Person erhält keine E-Mail')}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Picker-Modal */}
      {pickerOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setPickerOpen(false)}>
          <div
            className="bg-[var(--bg-card,#fff)] rounded-2xl w-full max-w-md max-h-[75vh] flex flex-col shadow-xl"
            onClick={ev => ev.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-[var(--border-default,#E5E7EB)]">
              <h3 className="text-sm font-bold text-[var(--text-primary)]">
                {t('notifEmpf.personWaehlen', 'Personen auswählen')}
              </h3>
              <button onClick={() => setPickerOpen(false)} className="p-1.5 rounded-lg hover:bg-[var(--bg-inset,#F8F9FC)]">
                <X size={16} className="text-[var(--text-tertiary)]" />
              </button>
            </div>
            <div className="px-5 py-3 border-b border-[var(--border-default,#E5E7EB)]">
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-default,#E5E7EB)]">
                <Search size={14} className="text-[var(--text-tertiary)]" />
                <input
                  value={pickerSuche}
                  onChange={ev => setPickerSuche(ev.target.value)}
                  placeholder={t('notifEmpf.suchen', 'Name oder E-Mail suchen…')}
                  className="flex-1 bg-transparent text-sm outline-none text-[var(--text-primary)]"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-3">
              {pickerGefiltert.length === 0 && (
                <div className="text-center text-xs text-[var(--text-tertiary)] py-6">
                  {t('notifEmpf.keineTreffer', 'Keine Personen gefunden')}
                </div>
              )}
              {pickerGefiltert.map(p => (
                <label
                  key={p.id}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-[var(--bg-inset,#F8F9FC)] cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={!!pickerSel[p.id]}
                    onChange={ev => setPickerSel(s => ({ ...s, [p.id]: ev.target.checked }))}
                    className="accent-[#338edc]"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-[var(--text-primary)] truncate">{p.mitarbeiter_name}</div>
                    <div className="text-xs text-[var(--text-tertiary)] truncate">
                      {p.email || t('notifEmpf.keineEmail', 'keine E-Mail hinterlegt')}
                    </div>
                  </div>
                  {p.telegram_erreichbar
                    ? <span className="text-xs text-emerald-600 shrink-0">Telegram ✓</span>
                    : <span className="text-xs text-[var(--text-muted,#9CA3AF)] shrink-0">Telegram –</span>}
                </label>
              ))}
            </div>
            <div className="px-5 py-3 border-t border-[var(--border-default,#E5E7EB)] flex justify-end gap-2">
              <button
                onClick={() => setPickerOpen(false)}
                className="px-3 py-1.5 rounded-lg text-sm text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)]"
              >
                {t('common.cancel', 'Abbrechen')}
              </button>
              <button
                onClick={addSelected}
                disabled={!Object.values(pickerSel).some(Boolean)}
                className="px-3 py-1.5 rounded-lg text-sm font-medium bg-[#338edc] text-white hover:bg-[#2b7bc4] disabled:opacity-40"
              >
                {t('notifEmpf.hinzufuegen', 'Hinzufügen')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
